import type { ReactNode } from "react";
import type { StatusTone } from "../../atoms/StatusDot/StatusDot.js";

/**
 * The wording that makes the guarantee explicit on every linked-work surface.
 * Exported so consumers and tests assert the exact string rather than a
 * paraphrase — the claim is a product requirement, not decoration.
 */
export const NO_PROOF_OF_IMPACT_NOTE =
  "Links show related work for context — they are not proof of impact.";

/**
 * Kind of execution work a link points at. Mirrors the frozen platform target
 * set; external tools (Jira, Confluence, …) arrive as `external_ref` plus a
 * `provider`, so a new tool never widens this union.
 */
export type WorkTargetKind = "solve" | "vsm" | "external_ref";

/** New work a host can offer to start from a panel or the link dialog. */
export type StartWorkKind = "solve" | "vsm";

/**
 * Both kinds, in the order the buttons have always shown. The default for
 * `startWorkKinds`, so a host written before the prop renders unchanged.
 */
export const START_WORK_KINDS: readonly StartWorkKind[] = ["solve", "vsm"];

/** Visible label of each start button. */
export const START_WORK_LABEL: Readonly<Record<StartWorkKind, string>> = {
  solve: "Start SOLVE",
  vsm: "Start VSM",
};

/**
 * One linked-work row. A pure view-model — this panel is data-blind
 * (ADR-0009 / reuse-first): Hoshin, SOLVE and VSM each map their own links onto
 * this shape, so the component carries no module vocabulary and no wire types.
 *
 * `status` is **optional on purpose**. Live work status has no source until the
 * readback API lands (PB-517), and a surface whose whole claim is "this is not
 * proof of impact" must not invent work state. Omitted means *render nothing* —
 * never a placeholder.
 */
export interface LinkedWorkRow {
  /** The link's own id — what {@link LinkedWorkPanelProps.onUnlink} receives. */
  id: string;
  kind: WorkTargetKind;
  /** For `external_ref`, the registering tool (e.g. `jira`) — label only. */
  provider?: string;
  title: string;
  /** Human-facing id, e.g. `solve-3020` or `OPS-421`. */
  reference?: string;
  owner?: { name: string };
  tags?: readonly string[];
  /** Rendered only when present. See the note on this interface. */
  status?: { label: string; tone: StatusTone };
  /** True when the link was created from the work side, not from strategy. */
  backlinked?: boolean;
  /** The linker's stated reason, in their words. */
  note?: string;
  /** Pre-formatted source context, e.g. `plan · priority · KPI`. The host
   * composes it and omits absent parts, so this is never a padded string. */
  context?: string;
  /**
   * Set when the work lives in a module that is off for this workspace
   * (ADR-0100, d3): the host's sentence, e.g. "VSM is turned off for this
   * workspace". The row stays — the link still exists and can still be
   * removed — but it is greyed, says so, and gets no `rowActionSlot`, so no
   * Open. Absent means the row is ordinary.
   */
  unavailableReason?: string;
}

export interface LinkedWorkPanelProps {
  readonly rows: readonly LinkedWorkRow[];
  /** Section label. */
  readonly heading?: string;
  /** Whether the viewer may add or remove links. Read-only surfaces (a
   * plan-level roll-up) pass false and get no unlink or link affordance. */
  readonly canManage?: boolean;
  /**
   * Whether the viewer may REMOVE an existing link, when that differs from
   * being able to add one. Defaults to {@link canManage}.
   *
   * It differs on a Hoshin plan: the server keeps unlink allowed on a closed or
   * archived plan on purpose — "an existing link must stay removable" — while
   * refusing new ones. One flag collapsed the two onto the stricter answer and
   * stranded a bad link on a closed plan forever.
   */
  readonly canUnlink?: boolean;
  readonly isLoading?: boolean;
  readonly error?: string | null;
  /**
   * Raises an intent to start new work (PB-240). The panel renders the buttons
   * and nothing else — what a "solve" means, and whether anything is created,
   * belongs entirely to the host.
   *
   * Starting work CREATES something; see {@link onOpenLinkDialog} for attaching
   * work that already exists. A surface may offer either, both, or neither.
   */
  readonly onStartWork?: (intent: "solve" | "vsm" | "jira") => void;
  /**
   * Which start buttons `onStartWork` gets. Default {@link START_WORK_KINDS}.
   * The host passes only the kinds whose module the workspace may use
   * (ADR-0100); empty offers none.
   */
  readonly startWorkKinds?: readonly StartWorkKind[];
  /**
   * Opens the host's link-existing-work picker (PB-5329).
   *
   * A panel-level "Link work" button was removed in `7641890ff` to match the
   * dialog's own action set, on the stated grounds that "the search-and-link
   * surface is unchanged and still reached from the bowling chart's Take
   * action". That holds for the bowling chart and nowhere else: on the TTI page
   * this panel IS the only entry, and its create buttons render only for an
   * at-risk item — so a healthy item offered no route to the picker at all,
   * under an empty state that tells the reader to link work. This restores the
   * route without restoring the create actions that were removed with it.
   */
  readonly onOpenLinkDialog?: () => void;
  /** Receives the row's link id. */
  readonly onUnlink?: (linkId: string) => void;
  /** Escape hatch for host-specific trailing content per row. Not called for
   * a row with `unavailableReason`. */
  readonly rowActionSlot?: (row: LinkedWorkRow) => ReactNode;
  /** Optional host-owned title link. Null falls back to plain text; never
   * called for a row with `unavailableReason`. */
  readonly rowTitleSlot?: (row: LinkedWorkRow) => ReactNode;
  /**
   * Links the host cannot name because their module is off (ADR-0100): one
   * greyed line each, e.g. "2 linked SOLVEs — SOLVE is turned off for this
   * workspace". Shown under the rows, or in place of the empty state when
   * there are none. Data-blind: the host writes every line.
   */
  readonly hiddenNotes?: readonly string[];
  readonly className?: string;
}

const KIND_LABEL: Record<WorkTargetKind, string> = {
  solve: "SOLVE",
  vsm: "VSM",
  external_ref: "LINK",
};

/** Badge label for a row — an external ref shows its provider, e.g. `JIRA`. */
export function kindLabel(row: LinkedWorkRow): string {
  if (row.kind === "external_ref" && row.provider) {
    return row.provider.toUpperCase();
  }
  return KIND_LABEL[row.kind];
}
